import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import {
  getLessons,
  CATEGORY_META,
  type Lesson,
  type LessonCategory,
} from "@/lib/training-api";
import { getProfile as getUserProfile } from "@/lib/profile-api";

const ALL_CATEGORIES: LessonCategory[] = [
  "signs",
  "speed",
  "right-of-way",
  "road-rules",
  "alcohol-drugs",
  "pedestrians",
  "parking",
  "weather",
];

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; lessons: Lesson[]; totalCompleted: number }
  | { kind: "error" };

export default function TrainingPage() {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<LessonCategory | "all">("all");
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [focusState, setFocusState] = useState<string>("");
  const [homeCountry, setHomeCountry] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profile, data] = await Promise.all([
          getUserProfile(),
          getLessons(),
        ]);
        if (cancelled) return;
        setFocusState(profile?.current_focus_state ?? "");
        setHomeCountry(profile?.home_country ?? "");
        const completed = data.lessons.filter((l) => l.completed).length;
        setLoadState({ kind: "ready", lessons: data.lessons, totalCompleted: completed });
      } catch {
        if (!cancelled) setLoadState({ kind: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = loadState.kind === "ready"
    ? (activeCategory === "all"
      ? loadState.lessons
      : loadState.lessons.filter((l) => l.category === activeCategory))
    : [];

  const total = loadState.kind === "ready" ? loadState.lessons.length : 0;
  const completed = loadState.kind === "ready" ? loadState.totalCompleted : 0;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <LearnerPortalShell pageTitle={t("nav.training")}>
      <div className="bg-slate-50">
      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-brand-primary via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-8 sm:py-16">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-widest text-slate-400">
                {t("training.hero.eyebrow")}
              </p>
              <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                {t("training.hero.titleLine1")}<br />
                <span className="text-brand-accent">{t("training.hero.titleLine2")}</span>
              </h1>
              <p className="mt-4 max-w-xl text-base text-slate-300">
                {homeCountry
                  ? t("training.hero.subtitleWithCountry", { country: homeCountry })
                  : t("training.hero.subtitle")}
              </p>
              {focusState && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm text-slate-200 backdrop-blur">
                  🎯{" "}
                  <span>
                    {t("training.hero.preparingFor", { state: focusState })}
                  </span>
                </div>
              )}
            </div>

            {/* Progress ring */}
            {loadState.kind === "ready" && (
              <div className="shrink-0 flex flex-col items-center gap-2">
                <div className="relative h-28 w-28">
                  <svg className="h-28 w-28 -rotate-90" viewBox="0 0 112 112">
                    <circle
                      cx="56" cy="56" r="48"
                      fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10"
                    />
                    <circle
                      cx="56" cy="56" r="48"
                      fill="none"
                      stroke="#f59e0b"
                      strokeWidth="10"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 48}`}
                      strokeDashoffset={`${2 * Math.PI * 48 * (1 - pct / 100)}`}
                      className="transition-all duration-700"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-2xl font-bold text-white">{pct}%</span>
                    <span className="text-xs text-slate-400">
                      {t("training.progress.completeLabel")}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-slate-400">
                  {t("training.progress.count", { completed, total })}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Category Tabs ─────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
            <button
              onClick={() => setActiveCategory("all")}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                activeCategory === "all"
                  ? "bg-brand-primary text-white shadow-sm"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {t("training.tabs.allLessons")}
            </button>
            {ALL_CATEGORIES.map((cat) => {
              const meta = CATEGORY_META[cat];
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    activeCategory === cat
                      ? "bg-brand-primary text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {meta.emoji} {t(meta.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Lesson Grid ───────────────────────────────────────────────── */}
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        {loadState.kind === "loading" && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-56 animate-pulse rounded-2xl bg-slate-200" />
            ))}
          </div>
        )}

        {loadState.kind === "error" && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-600">
            {t("training.error.failedToLoad")}
          </div>
        )}

        {loadState.kind === "ready" && filtered.length === 0 && (
          <div className="py-24 text-center text-slate-500">
            {t("training.error.noLessonsInCategory")}
          </div>
        )}

        {loadState.kind === "ready" && filtered.length > 0 && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((lesson) => (
              <LessonCard key={lesson.id} lesson={lesson} />
            ))}
          </div>
        )}
      </div>
      </div>
    </LearnerPortalShell>
  );
}

function LessonCard({ lesson }: { lesson: Lesson }) {
  const { t } = useTranslation();
  const meta = CATEGORY_META[lesson.category];
  return (
    <Link
      to={`/training/${lesson.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md no-underline"
    >
      {/* Thumbnail */}
      <div
        className={`flex h-36 items-center justify-center text-6xl bg-gradient-to-br ${lesson.colorFrom} ${lesson.colorTo}`}
      >
        {lesson.emoji}
        {lesson.completed && (
          <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-green-500 text-white shadow">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </span>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
            {meta.emoji} {t(meta.labelKey)}
          </span>
          <span className="ml-auto text-xs text-slate-400">
            {t("training.lessonCard.durationMin", { minutes: lesson.duration_min })}
          </span>
        </div>
        <h3 className="font-semibold text-slate-900 leading-snug group-hover:text-brand-primary transition-colors">
          {lesson.title}
        </h3>
        <p className="text-sm text-slate-500 line-clamp-2">{lesson.summary}</p>
        <div className="mt-auto pt-2">
          <span
            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
              lesson.completed
                ? "bg-green-50 text-green-700"
                : "bg-brand-primary/5 text-brand-primary"
            }`}
          >
            {lesson.completed
              ? t("training.lessonCard.statusCompleted")
              : t("training.lessonCard.statusStart")}
          </span>
        </div>
      </div>
    </Link>
  );
}
